import React from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Form, Field, FormRenderProps } from 'react-final-form';
import { ValidationErrors } from 'final-form';
import FontAwesomeIcon from 'react-fontawesome';
import { useTranslation } from 'react-i18next';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import Sidebar from './Sidebar';
import { AppStore, Store } from './types';
import { resetPassword, enqueueNotification } from './actions/Actions';
import { ACCOUNT_NS } from './Constants';

type FormValues = Pick<AppStore['user'], 'userName'> & {
    password: string;
    repeatPassword: string;
};

const Account: React.FC<RouteComponentProps> = ({ history }) => {
    const user = useSelector(({ app }: Store) => app.user);
    const [loading, setLoading] = React.useState(false);
    const { t } = useTranslation();
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();

    const validateForm = React.useCallback(
        (values: FormValues) => {
            const errors: ValidationErrors = {};

            if (!values.password?.trim()) {
                errors.password = t('required_field');
            }

            if (values.password !== values.repeatPassword) {
                errors.repeatPassword = t(`${ACCOUNT_NS}.password_mismatch`);
            }

            return errors;
        },
        [t]
    );

    const renderForm = React.useCallback(
        ({ handleSubmit, submitting, validating }: FormRenderProps) => (
            <form onSubmit={handleSubmit} className='form'>
                <label className='form-field'>
                    <label className='small-wide'>{t('username')}:</label>
                    <Field name='userName' type='text'>
                        {({ input }) => (
                            <div className='field-input'>
                                <input {...input} disabled />
                            </div>
                        )}
                    </Field>
                </label>
                <label className='form-field'>
                    <label className='small-wide'>{t('password')}:</label>
                    <Field name='password' type='password'>
                        {({ input, meta }) => (
                            <div className='field-input'>
                                <input {...input} autoComplete='new-password' />
                                {meta.error && meta.touched && (
                                    <FontAwesomeIcon
                                        className='error-icon'
                                        name='exclamation-circle'
                                        title={meta.error}
                                    />
                                )}
                            </div>
                        )}
                    </Field>
                </label>
                <label className='form-field'>
                    <label className='small-wide'>
                        {t(`${ACCOUNT_NS}.repeat_password`)}:
                    </label>
                    <Field name='repeatPassword' type='password'>
                        {({ input, meta }) => (
                            <div className='field-input'>
                                <input {...input} autoComplete='new-password' />
                                {meta.error && meta.touched && (
                                    <FontAwesomeIcon
                                        className='error-icon'
                                        name='exclamation-circle'
                                        title={meta.error}
                                    />
                                )}
                            </div>
                        )}
                    </Field>
                </label>
                <div className='action-buttons'>
                    <div>
                        <button
                            className='save'
                            type='submit'
                            disabled={submitting || validating}>
                            {t('save')}
                        </button>
                        {loading && (
                            <FontAwesomeIcon name='spinner' className='fa-spin' />
                        )}
                    </div>
                </div>
            </form>
        ),
        [loading, t]
    );

    const handleSubmit = React.useCallback(
        (values: FormValues) => {
            setLoading(true);

            dispatch(resetPassword(values))
                .then(() => {
                    setLoading(false);
                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message: t(`${ACCOUNT_NS}.successful_update`),
                            options: { variant: 'success' }
                        })
                    );

                    history.push('/');
                })
                .catch((e: Error) => {
                    setLoading(false);
                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message: e.message,
                            options: { variant: 'error' }
                        })
                    );
                });
        },
        [dispatch, t, history]
    );

    return (
        <div className='admin-form main-content account-configuration'>
            <Sidebar />
            <div className='account-form'>
                <Form
                    initialValues={{ ..._.pick(user, 'userName') }}
                    validate={validateForm}
                    render={renderForm}
                    onSubmit={handleSubmit}
                />
            </div>
        </div>
    );
};

export default Account;
